import StatusTag from '@/components/_common/StatusTag';
import {
  useChangeWorkOrderItemRestore,
  useWorkOrderItems,
  WORKORDER_HAS_REMOVED_ITENS_KEY,
  WORKORDER_ITEMS_QUERY_KEY,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import {
  WorkOrderItem,
  WorkOrderItemStockStatus,
  WorkOrderItemType,
  workorderTypeColor,
  workorderTypeItem,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import {
  Divider,
  Modal,
  ModalProps,
  Popover,
  Table,
  Typography,
  notification,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { Info } from 'react-feather';
import { Group } from '@/components/_common';

type Props = Omit<ModalProps, 'onCancel'> & {
  onCancel: VoidFunction;
};

type TableData = WorkOrderItem & {
  kitDiscountValue?: number;
  kitTotalValue?: number;
  kitStatus?: WorkOrderItemStockStatus[];
  disableDelete?: boolean;
  children?: (WorkOrderItem & { disableDelete?: boolean })[];
};

const RestoreItemsModal: React.FC<Props> = (props) => {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const {
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const workOrderItems = useWorkOrderItems(
    props.open ? complement?.id : undefined,
    true,
    undefined,
    true,
  );
  const items = workOrderItems.data?.data;

  const [kitRestoring, setKitRestoring] = useState(false);

  const itemRestore = useChangeWorkOrderItemRestore();
  const [itemsSelected, setItemsSelected] = useState<TableData[]>([]);

  const tableData = useMemo(
    () =>
      items
        ?.filter((item) => !item.isItemRejectedByCustomer)
        ?.reduce((acc, curr) => {
          if (curr.kitId && acc.some((item) => item.kitId === curr.kitId)) {
            return acc.map((item) => {
              if (item.kitId === curr.kitId) {
                return {
                  ...item,
                  children: [...(item.children || []), curr],
                };
              }
              return item;
            });
          }

          if (curr.kitId) {
            return [
              ...acc,
              {
                ...curr,
                id: curr.kitId,
                description: String(curr.kitName),
                sku: '',
                type: WorkOrderItemType.Kit,
                children: [{ ...curr, disableDelete: true }],
              },
            ];
          }

          return [...acc, curr];
        }, [] as TableData[]),
    [items],
  );

  const itemsRejectedByCustomer = items?.filter(
    (item) => item.isItemRejectedByCustomer,
  );

  const renderTag = (item: WorkOrderItem): Record<string, React.ReactNode> => ({
    [WorkOrderItemType.Kit]: (
      <StatusTag color={workorderTypeColor[item.type]} label={`Kit`} />
    ),
    [WorkOrderItemType.Part]: (
      <StatusTag
        color={workorderTypeColor[item.type]}
        label={workorderTypeItem[`${item.type}/${item.origin}`]}
      />
    ),
    [WorkOrderItemType.Service]: (
      <StatusTag
        color={workorderTypeColor[item.type]}
        label={workorderTypeItem[`${item.type}/${item.origin}`]}
      />
    ),
  });

  const columns: ColumnsType<WorkOrderItem> = [
    {
      dataIndex: 'type',
      title: intl.formatMessage({
        id: 'workOrders.domain.items.type',
      }),
      width: 200,
      ellipsis: true,
      render: (_, item) => renderTag(item)[item.type] || '-',
    },
    {
      key: 'manufacturingCode',
      dataIndex: 'manufacturingCode',
      title: 'Código',
      width: 150,
      render: (manufacturingCode, item) =>
        item.type === WorkOrderItemType.Kit ? (
          <strong>-</strong>
        ) : (
          manufacturingCode || '-'
        ),
    },
    {
      key: 'description',
      dataIndex: 'description',
      title: 'Descrição',
      render: (value, item) => (
        <EllipsisHoverToggle
          style={
            item.type === WorkOrderItemType.Kit ? { fontWeight: 'bold' } : {}
          }
        >
          {!item.isItemRejectedByCustomer ? (
            value
          ) : (
            <Group gap={5} align="start">
              <Popover
                trigger="click"
                content={item.itemRejectedReason ?? 'Motivo não informado'}
              >
                <Info size={16} style={{ cursor: 'pointer' }} />
              </Popover>

              {value}
            </Group>
          )}
        </EllipsisHoverToggle>
      ),
    },
  ];

  async function handleSubmit() {
    try {
      if (itemsSelected.length && complement) {
        const item = itemsSelected[0] as TableData | undefined;

        if (item?.type === WorkOrderItemType.Kit) {
          setKitRestoring(true);

          const results = await Promise.all(
            item?.children?.map(async (child) => {
              try {
                return await itemRestore.mutateAsync({
                  complementId: complement.id,
                  id: child.id,
                });
              } catch {
                return null;
              }
            }) || [],
          );

          if (results.some((result) => result === null)) {
            notification.warning({
              message: 'Restauração parcial do kit',
              description:
                'Alguns itens do kit não foram restaurados devido a erros.',
            });
          } else {
            notification.success({
              message: 'Kit restaurado',
              description:
                'Os itens do kit selecionado foram restaurados com sucesso!',
            });
          }

          setKitRestoring(false);
        } else if (item?.id) {
          await itemRestore.mutateAsync({
            complementId: complement.id,
            id: item.id,
          });
          const itemType =
            item.type === WorkOrderItemType.Part ? 'A peça' : 'O serviço';

          notification.success({
            message: 'Item restaurado',
            description: (
              <>
                {itemType} <strong>{item.description}</strong> foi restaurado
                com sucesso!
              </>
            ),
          });
        }

        setItemsSelected([]);
      }
    } finally {
      queryChanel({
        invalidateQueries: [[`${WORKORDER_ITEMS_QUERY_KEY}/verify`]],
        resetQueries: [
          [WORKORDER_ITEMS_QUERY_KEY],
          [WORKORDER_HAS_REMOVED_ITENS_KEY, { complementId: complement?.id }],
        ],
      });
    }
  }

  function handleCancel() {
    props.onCancel();
    setItemsSelected([]);
  }

  return (
    <>
      {kitRestoring && (
        <ModalLoadingOverlay messages={['Restaurando os itens do kit...']} />
      )}
      <Modal
        {...props}
        title="Selecione o item a ser restaurado"
        width={800}
        onCancel={handleCancel}
        onOk={handleSubmit}
        okText="Restaurar"
        confirmLoading={itemRestore.isLoading}
      >
        <Table
          dataSource={tableData}
          columns={columns}
          loading={workOrderItems.isLoading}
          rowKey={(row) => row.id}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: itemsSelected.map((item) => item.id),
            onChange: (_, rows) => setItemsSelected(rows),
            getCheckboxProps(record) {
              if (record.kitId && record.type !== WorkOrderItemType.Kit) {
                return { disabled: true };
              }
              return {};
            },
          }}
          pagination={false}
        />

        <Divider />

        <Table
          title={() => (
            <Typography.Title level={4} style={{ margin: 0 }}>
              Itens recusados pelo cliente
            </Typography.Title>
          )}
          loading={workOrderItems.isLoading}
          dataSource={itemsRejectedByCustomer}
          columns={columns}
          rowKey={(row) => row.id}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: itemsSelected.map((item) => item.id),
            onChange: (_, rows) => setItemsSelected(rows),
          }}
          pagination={false}
        />
      </Modal>
    </>
  );
};

export default RestoreItemsModal;
